import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Linkedin, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// BastionGuard Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-ghost/artifacts/h0ankv1n_logo.png";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const footerLinks = {
    company: [
      { name: "About Us", path: "/about" },
      { name: "Products", path: "/products/sva-detox" },
      { name: "Blog", path: "/blog" },
      { name: "Contact", path: "/contact" },
    ],
    legal: [
      { name: "Privacy Policy", path: "/products/sva-detox/privacy" },
      { name: "Terms of Service", path: "/products/sva-detox/terms" },
      { name: "Refund Policy", path: "/products/sva-detox/refunds" },
    ],
  };

  return (
    <footer className="bg-[#0B0F19] border-t border-slate-800/50" data-testid="footer">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center mb-6" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="BastionGuard Medical Technologies" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="body-base mb-6">
              Pioneering the future of healthcare through innovative medical devices and digital health solutions.
            </p>
            <div className="flex gap-4">
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-slate-800/50 border border-slate-700 flex items-center justify-center text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-all duration-200"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-slate-800/50 border border-slate-700 flex items-center justify-center text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-all duration-200"
                data-testid="footer-twitter"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Join the Future</h4>
            <p className="body-base mb-4">
              Subscribe for updates on healthcare innovation.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3" data-testid="footer-newsletter-form">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark flex-1"
                  data-testid="footer-newsletter-email"
                />
                <Button
                  type="submit"
                  className="btn-primary px-4"
                  disabled={isLoading}
                  data-testid="footer-newsletter-submit"
                >
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-800/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500" data-testid="footer-copyright">
            © {new Date().getFullYear()} BastionGuard Medical Technologies Private Limited. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Mail className="w-4 h-4 text-cyan-500" />
            <a 
              href="mailto:info@bastionguardhealthcare.in" 
              className="hover:text-cyan-400 transition-colors"
              data-testid="footer-email"
            >
              info@bastionguardhealthcare.in
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
